import { observer } from "mobx-react";
import { useEffect } from "react";
import { AbnEntityItemSection } from "../AustralianBusinessRegistryInformation/AbnEntityItemSection";
import { IconDisplay, IconTextBox } from "../TextBox/IconTextBox";
import { BusinessValidation } from "./BusinessValidationModel";
import { debounce } from "lodash";

type AbnInputProps = {
	model: BusinessValidation;
	isRequired?: boolean;
	onAfterChange?: (newValue: BusinessValidation) => void;
	additionalInputPropTypes?: React.InputHTMLAttributes<Element>;
}

export const AbnInput = observer((props: AbnInputProps) => {

	const {
		model,
		isRequired = false,
		onAfterChange,
		additionalInputPropTypes,
	} = props;

	useEffect(() => {
		// Validate and fetch the initial data on mount
		async function onMount() {
			if ((model.abn?.length ?? 0) > 0) await handleAbnChange();
		}
		onMount();
	}, [model.abn]);

	const handleAbnChange = async () => {
		await model.runAbnValidation();
		if (onAfterChange) onAfterChange(model);
	}

	const abnIsSet = model.abn != null && model.abn.length !== 0;
	const iconType = abnIsSet
		? model.abnExtraDetails.statusCode === 'Active' ? IconDisplay.Valid : IconDisplay.Error
		: undefined;

	return (
		<div className="business-number-input">
			<IconTextBox
				inputClassName="business-number-icon-input"
				inputProps={{
					type: "number",
					...additionalInputPropTypes
				}}
				model={model}
				modelProperty="abn"
				placeholder="ABN"
				// Dev note: Can't support readonly mode as it will not set the fields below
				// isReadOnly
				defaultToUndefined
				onAfterChange={handleAbnChange}
				onChangeAndBlur={debounce(handleAbnChange, 300)}
				errors={model.errors.abn}
				isRequired={isRequired}
				displayIconContainer={model.abnFinishedValidating}
				iconDisplay={iconType}
				label="Australian Business Number (ABN)"
			/>
			<small className="field-details">
				<AbnEntityItemSection
					status={model.abnExtraDetails.statusCode}
					entityTypeCode={model.abnExtraDetails.typeCode}
					entityName={model.abnExtraDetails.name}
					source={model.abnExtraDetails.source}
					fallbackValue="-"
				/>
			</small>
		</div>
	);
});